<template>
  <div class="about-content">
    <!-- Header (samo kad je showHeader true) -->
    <div v-if="showHeader" class="content-header q-pa-sm">
      <div class="row items-center">
        <q-btn 
          v-if="showBackButton"
          flat 
          round 
          icon="arrow_back" 
          @click="$emit('back')"
          class="q-mr-sm"
        />
        <div class="text-h5 text-weight-medium text-grey-8">{{$t('About')}}</div>
        <q-space />
        <q-btn 
          v-if="showCloseButton"
          flat 
          round 
          icon="close" 
          @click="$emit('close')"
        />
      </div>
    </div>

    <!-- <q-banner :class="($q.dark.isActive ? 'bg-orange-9 text-white' : 'bg-warning text-white') + ' q-ma-sm q-pa-md'" rounded> -->
    <q-banner class="bg-orange-9 text-white q-ma-sm q-pa-md" rounded>
      <template v-slot:avatar>
        <q-icon name="warning" color="white" />
      </template>
      <span class="text-weight-bold">{{$t('Important')}}:</span>
  
      <span>{{$t('This is a pre-beta release. Use only for testing purposes!')}}</span>
    </q-banner>

    <!-- Project Information -->
    <div class="q-pa-sm">
      <!-- AI4SoilHealth Project Card -->
      <q-card class="project-card q-mb-lg" flat bordered>
        <q-card-section class="bg-primary text-white">
          <div class="row items-center">
            <img 
              src="/android-chrome-512x512.png" 
              alt="AI4SoilHealth Logo"
              class="project-logo q-mr-md"
            />
            <div>
              <div class="text-h6 text-weight-medium">AI4SoilHealth {{$t('Project')}}</div>
              <div class="text-body2 opacity-80">{{$t('Horizon')}} {{$t('Europe')}} {{$t('Research Programme')}}</div>
            </div>
          </div>
        </q-card-section>
        
        <q-card-section>
          <div class="q-mb-md">
            <q-btn 
              type="a"
              href="https://ai4soilhealth.eu/"
              target="_blank"
              no-caps
              flat
              color="primary"
              icon="launch"
              label="ai4soilhealth.eu"
              class="text-weight-medium project-link"
            />
            <div class="text-body2 q-mt-sm text-grey-8">
              {{$t('is one of a group of Horizon Europe funded projects which fit under the')}}
              <strong class="text-primary">{{$t("EU's Soil Health Mission for 2030")}}</strong>.
            </div>
          </div>
          
          <q-separator class="q-my-md" />
          
          <!-- Grant Information -->
          <div class="grant-info q-mb-md">
            <div class="row items-center q-mb-sm">
              <q-icon name="account_balance" color="positive" class="q-mr-sm" />
              <span class="text-weight-medium text-grey-8">{{$t('Grant Information')}}</span>
            </div>
            <div class="text-body2 text-grey-7 q-ml-lg">
              {{$t("This project has received funding from the European Union's Horizon Europe research and innovation programme under grant agreement")}}
              <q-chip 
                color="positive" 
                text-color="white" 
                dense 
                class="text-weight-bold"
              >
                {{$t('No')}}. 101086179
              </q-chip>
            </div>
          </div>
        </q-card-section>
      </q-card>

      <!-- EU Funding Disclaimer -->
      <q-card flat bordered class="q-mb-lg disclaimer-card">
        <q-card-section class="q-pa-lg">
          <div class="row items-start q-gutter-md">
            <div class="eu-flag-container">
              <img 
                src="logo/eu.png" 
                alt="EU Flag"
                class="flag-img"
              />
            </div>
            <div class="flex-1">
              <div class="text-weight-medium q-mb-sm text-blue-8">
                {{$t('Funded by the European Union')}}
              </div>
              <div class="text-body2 text-grey-7 disclaimer-text">
                {{$t('Views and opinions expressed are however those of the author(s) only and do not necessarily reflect those of the European Union or European Research Executive Agency')}}. 
                {{$t('Neither the European Union nor the granting authority can be held responsible for them')}}.
              </div>
            </div>
          </div>
        </q-card-section>
      </q-card>

      <!-- Quick Links -->
      <q-list separator class="rounded-borders">
        <q-item-label header class="text-weight-medium text-grey-8">
          <q-icon name="link" class="q-mr-sm" />
          {{$t('Quick Links')}}
        </q-item-label>
        
        <q-item 
          clickable 
          v-ripple
          @click="handleAboutClick"
        >
          <q-item-section avatar>
            <q-icon name="help_outline" color="grey-6" size="md" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{$t('About the Platform')}}
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{$t('Learn more about our soil health platform')}}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="chevron_right" color="grey-4" />
          </q-item-section>
        </q-item>

        <q-item 
          clickable 
          v-ripple
          @click="handleEcoDataCubeClick"
        >
          <q-item-section avatar>
            <q-icon name="public" color="grey-6" size="md" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              EcoDataCube
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{$t('More pan-EU environmental data can be obtained in EcoDataCube.eu')}}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="chevron_right" color="grey-4" />
          </q-item-section>
        </q-item>

        <q-item 
          clickable 
          v-ripple
          @click="handleSourceCodeClick"
        >
          <q-item-section avatar>
            <q-icon name="code" color="grey-6" size="md" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{$t('Source Code')}}
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{$t('The code developed here is available on GitHub')}}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="chevron_right" color="grey-4" />
          </q-item-section>
        </q-item>

        <q-item 
          clickable 
          v-ripple
          @click="handlePrivacyClick"
        >
          <q-item-section avatar>
            <q-icon name="security" color="grey-6" size="md" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{$t('Privacy Policy')}}
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{$t('How we handle your data and privacy')}}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="chevron_right" color="grey-4" />
          </q-item-section>
        </q-item>

        <q-item 
          clickable 
          v-ripple
          @click="handleTermsClick"
        >
          <q-item-section avatar>
            <q-icon name="article" color="grey-6" size="md" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{$t('Terms of Service')}}
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{$t('Terms and conditions of use')}}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="chevron_right" color="grey-4" />
          </q-item-section>
        </q-item>
      </q-list>

      <!-- Powered by Section -->
      <div class="powered-by-section q-mt-lg q-pa-sm text-center">
        <div class="text-caption text-grey-6 q-mb-sm">{{$t('Powered by')}}</div>
        <!-- <a 
          href="https://www.fer.unizg.hr/en" 
          target="_blank"
          rel="noopener noreferrer"
          class="fer-link"
        >
          <img 
            src="logo/ferlogo.svg" 
            alt="FER Logo"
            class="fer-logo"
          />
        </a> -->
        <div class="logos-container">
          <a 
            href="https://www.fer.unizg.hr/en" 
            target="_blank"
            rel="noopener noreferrer"
            class="logo-link"
          >
            <img 
              src="logo/ferlogo.svg" 
              alt="FER Logo"
              class="partner-logo"
            />
          </a>
          <a 
            href="https://https://multione.hr/" 
            target="_blank"
            rel="noopener noreferrer"
            class="logo-link"
          >
            <img 
              src="logo/multione-logo.svg" 
              alt="Partner 2 Logo"
              class="partner-logo"
            />
          </a>
          <a 
            href="https://partner3.com" 
            target="_blank"
            rel="noopener noreferrer"
            class="logo-link"
          >
            <img 
              src="logo/opengeohub-logo.png" 
              alt="Partner 3 Logo"
              class="partner-logo"
            />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  showHeader: {
    type: Boolean,
    default: false
  },
  showBackButton: {
    type: Boolean,
    default: false
  },
  showCloseButton: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['back', 'close', 'about-click', 'privacy-click', 'terms-click'])

const handleAboutClick = () => {
  emit('about-click')
}

const handleSourceCodeClick = () => {
  const url = 'https://github.com/AI4SoilHealth/'
  window.open(url, '_blank')
}

const handleEcoDataCubeClick = () => {
  const url = 'https://EcoDataCube.eu'
  window.open(url, '_blank')
}

const handlePrivacyClick = () => {
  emit('privacy-click')
}

const handleTermsClick = () => {
  emit('terms-click')
}
</script>

<style lang="scss" scoped>

.text-grey-8 {
  body.body--dark & {
    color: white !important;
  }
}

.text-grey-7 {
  body.body--dark & {
     color: $grey-6 !important;
  }
}

body.body--dark {
  .text-primary {
    // color: #4CAF50 !important; // svjetlija zelena
    // ili
    color: #66BB6A !important;
    // ili
    // color: #81C784 !important;
  }
}

.about-content {
  .content-header {
    border-bottom: 1px solid #e0e0e0;

    
  }
}

.project-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.disclaimer-card {
  background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
  border-left: 4px solid #1976d2;

  body.body--dark & {
    background: linear-gradient(135deg, #1e2a3a 0%, #263545 100%);
    border-left: 4px solid #42a5f5;
  }
  
  .disclaimer-text {
    font-style: italic;
    line-height: 1.5;
  }
  
  .eu-flag-container {
    .flag-img {
      width: 60px;
      height: 40px;
      object-fit: contain;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      border: 1px solid rgba(255,255,255,0.2);
    }
  }
}

.powered-by-section {
  border-top: 1px solid #e0e0e0;
  
    .logos-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px; // dovoljno razmaka između logoa
    flex-wrap: wrap;
  }
  
  .logo-link {
    text-decoration: none;
    display: inline-block;
    transition: transform 0.2s ease, opacity 0.2s ease;
    
    &:hover {
      transform: translateY(-2px);
      opacity: 0.8;
    }
    
    .partner-logo {
      height: 40px;
      width: auto;
      max-width: 100px;
      object-fit: contain;

      body.body--dark & {
        filter: brightness(0) invert(1);
      }
    }
  }

  // .fer-link {
  //   text-decoration: none;
  //   display: inline-block;
  //   transition: transform 0.2s ease;
    
  //   &:hover {
  //     transform: translateY(-2px);
  //   }
    
  //   .fer-logo {
  //     height: 40px;
  //     width: auto;
  //     object-fit: contain;

  //      body.body--dark & {
  //       filter: brightness(0) invert(1);
  //     }
  //   }
  // }
}
</style>